<script>
import { ValidationProvider } from 'vee-validate';
import { mapGetters } from 'vuex';
import { create, update } from '@/api/plan_partners';
import { get } from 'lodash';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

export default {
  name: 'PlansPartnerForm',
  components: {
    ValidationProvider,
  },

  props: {
    plan: {
      type: Object,
      default: () => ({
        id: null,
        name: null,
        discountUnit: '%',
        discount: '',
        commissionUnit: '%',
        commission: '',
        isSpecial: false,
        hasPartners: false,
      }),
    },
  },

  data() {
    return {
      isLoading: false,
      form: {
        id: this.plan.id,
        name: this.plan.name,
        discountUnit: this.plan.discountUnit,
        discount: this.plan.discount.toString(),
        commissionUnit: this.plan.commissionUnit,
        commission: this.plan.commission.toString(),
        isSpecial: this.plan.isSpecial,
        hasPartners: this.plan.hasPartners,
      },

      mask: createNumberMask({
        prefix: '',
        allowDecimal: false,
        includeThousandsSeparator: false,
        allowNegative: false,
      }),

      masks: {
        numeral: true,
        numeralThousandsGroupStyle: 'thousand',
        numeralDecimalScale: 0,
        numeralPositiveOnly: true,
      },

    };
  },

  computed: {
    ...mapGetters(['authAuthenticator']),
    commission: {
      get() {
        return this.form.commission;
      },
      set(newValue) {
        if (newValue) {
          // eslint-disable-next-line no-param-reassign
          newValue = newValue.replace(/,/, '');
        }
        this.form.commission = newValue;
      },
    },
    discount: {
      get() {
        return this.form.discount;
      },
      set(newValue) {
        if (newValue) {
          // eslint-disable-next-line no-param-reassign
          newValue = newValue.replace(/,/, '');
        }
        this.form.discount = newValue;
      },
    },

    isUpdate() {
      return this.form.id !== null;
    },
  },

  methods: {
    async submit() {
      try {
        this.isLoading = true;

        this.form.name = this.form.name.replace(/\s+/g, ' ');

        const { data } = this.form.id
          ? await update({ ...this.form })
          : await create({ ...this.form });

        this.$emit('submit', { data });

        this.$buefy.toast.open({
          message: this.isUpdate ? 'Plan was updated successfully' : 'Plan was created successfully',
          type: 'is-success',
          duration: 5000,
        });

        this.$emit('close');
      } catch (error) {
        this.$buefy.snackbar.open({
          message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    errorMessage(field, filedRules) {
      const failedRule = Object.keys(filedRules).at(0);
      if (failedRule === 'max_value' || failedRule === 'min_value') {
        return ` Enter a value between 0 and ${field === '$' ? '99,999' : '100'}`;
      }

      if (failedRule === 'required') {
        return 'This field is required ';
      }
      return '';
    },
  },
};
</script>

<template>
  <validation-observer v-slot="{ handleSubmit }">
    <div class="modern-card modal-card">
      <b-loading :is-full-page="false" :active="isLoading" :can-cancel="false"></b-loading>
      <header class="modern-card__header">{{ isUpdate ? 'Edit plan' : 'New plan' }}
        <b-button class="modern-card__close-button" aria-label="close" icon-right="close"
                  @click="$emit('close')" />
      </header>
      <section class="modern-card__body">
        <form class="modern-modal-form">
          <validation-provider v-slot="{ errors }" rules="required">
            <b-field label="Name*"
                     :type="{ 'is-danger': errors && errors.length }"
                     :message="errors + [ '&nbsp;' ]">
              <b-input size="is-medium" v-model.trim="form.name" maxlength="100" has-counter />
            </b-field>
          </validation-provider>
          <validation-provider>
            <b-field style="margin-bottom: .7em">
              <b-switch size="is-medium"
                        :disabled="!authAuthenticator({resource: 'manage-plans', code: 'special'})"
                        class="enh-switch"
                        type="is-green" v-model="form.isSpecial">
                Special plan (invisible to managers without special privilege)
              </b-switch>
            </b-field>
          </validation-provider>

          <template v-if="!form.hasPartners">
            <b-field grouped
                     class="m-group"
                     label="Discount for partner’s customers (for all time)"
                     custom-class="m-label">
              <b-field>
                <b-select size="is-medium"
                          v-model="form.discountUnit" @change.native="form.discount = null">
                  <option value="%">%</option>
                  <option value="$">$</option>
                </b-select>
              </b-field>
              <validation-provider v-if="form.discountUnit === '$'"
                                   v-slot="{ errors, failedRules }"
                                   :rules="{ required: true, min_value:0, max_value:99999, }">
                <b-field v-model="form.discount" :type="{ 'is-danger': errors.length }"
                         :message="errorMessage('$', failedRules) + [ '&nbsp;' ]">
                  <b-input size="is-medium" type="text"
                           v-cleave="masks" v-model="discount" />
                </b-field>
              </validation-provider>
              <validation-provider v-else
                                   v-slot="{ errors, failedRules }"
                                   :rules="{ required: true, min_value:0, max_value:100, }">
                <b-field v-model="form.discount" :type="{ 'is-danger': errors.length }"
                         :message="errorMessage('%', failedRules) + [ '&nbsp;' ]">
                  <b-input  size="is-medium" type="text"
                           v-cleave="masks" v-model="discount" />
                </b-field>
              </validation-provider>
            </b-field>
            <validation-provider>
              <b-field grouped
                       class="m-group"
                       label="Partner’s commissions"
                       custom-class="m-label">
                <b-field>
                  <b-select size="is-medium" v-model="form.commissionUnit"
                            @change.native="form.commission = null">
                    <option value="%">%</option>
                    <option value="$">$</option>
                  </b-select>
                </b-field>
                <validation-provider v-if="form.commissionUnit === '$'"
                                     v-slot="{ errors, failedRules }"
                                     :rules="{ required: true, min_value:0, max_value:99999, }">
                  <b-field v-model="form.commission" :type="{ 'is-danger': errors.length }"
                           :message="errorMessage('$', failedRules) + [ '&nbsp;' ]">
                    <b-input  size="is-medium" type="text"
                              v-cleave="masks" v-model="commission" />
                  </b-field>
                </validation-provider>
                <validation-provider v-else
                                     v-slot="{ errors, failedRules }"
                                     :rules="{ required: true, min_value:0, max_value:100, }">
                  <b-field v-model="form.commission" :type="{ 'is-danger': errors.length }"
                           :message="errorMessage('#', failedRules) + [ '&nbsp;' ]">
                    <b-input size="is-medium" type="text"
                              v-cleave="masks" v-model="commission" />
                  </b-field>
                </validation-provider>
              </b-field>
            </validation-provider>
          </template>
          <template v-else>
            <p>You can't edit the discount or commissions because the plan is being used by one or
              more partners.</p>
          </template>
        </form>
      </section>
      <footer class="modern-card__footer">
        <button type="button" class="button is-success outlined"
                @click="$emit('close')">Cancel</button>
        <button type="button" class="button is-success"
                @click="handleSubmit(submit)">{{ isUpdate ? 'Update' : 'Create' }}</button>
      </footer>
    </div>
  </validation-observer>
</template>

<style scoped lang="stylus">
@require "../../../assets/style/modern/variables.styl";
.m-group
  flex-flow wrap

::v-deep .switch
  span.control-label
    font-size 1rem
::v-deep .m-label
  flex 0 0 100%
</style>
